import { useEffect, useMemo, useState } from 'react'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { PointsBadge } from '@/components/PointsBadge'
import type {
  BadgeOverviewItem,
  BadgeUser,
  FootballMatch,
  UserBadgeInstance,
} from '@/features/poules/api'
import { usePouleAllBadges } from '@/features/poules/api'
import { getLocale, toIntlLocale } from '@/config/locale'
import { translate, useTranslation } from '@/i18n'
import { formatKickoffCompactDateTime } from '@/lib/kickoff'

type BadgeCategory = 'regular' | 'club'

export function PouleBadgesCard({
  pouleId,
  previewBadges,
}: {
  pouleId: number
  previewBadges: Array<Record<string, unknown>>
}) {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="block w-full rounded-[1.25rem] border border-white/[0.06] bg-[#0d1113] p-5 text-left transition hover:border-surface-600 hover:bg-[#151a1c] focus-visible:border-surface-500 focus-visible:bg-[#151a1c] focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d1113]"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
          {t('Mijn badges')}
        </p>

        {previewBadges.length > 0 ? (
          <div className="mt-4 grid grid-cols-3 gap-2">
            {previewBadges.slice(0, 6).map((badge, index) => (
              <div
                key={`${String(badge.id ?? badge.badge_id ?? index)}`}
                className="flex min-h-20 flex-col items-center justify-center rounded-[0.95rem] bg-[#111416] px-2 py-2.5"
                title={findPreviewLabel(badge) || t('Badge')}
              >
                <PreviewBadgeArtwork badge={badge} />
                <p className="mt-2 line-clamp-1 w-full text-center text-xs font-semibold text-white">
                  {findPreviewLabel(badge) || t('Badge')}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-[0.95rem] border border-[#202427] bg-[#111416] px-4 py-3 text-sm text-surface-400">
            {t('Je volledige badgecatalogus staat in deze poule klaar, ook als je nog niets hebt verdiend.')}
          </div>
        )}
      </button>

      {isOpen ? (
        <PouleBadgesOverlay pouleId={pouleId} onClose={() => setIsOpen(false)} />
      ) : null}
    </>
  )
}

function PouleBadgesOverlay({
  pouleId,
  onClose,
}: {
  pouleId: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const badgesQuery = usePouleAllBadges(pouleId, { enabled: true })
  const badges = badgesQuery.data?.data ?? []
  const [activeCategory, setActiveCategory] = useState<BadgeCategory>('regular')
  const [selectedBadge, setSelectedBadge] = useState<BadgeOverviewItem | null>(null)

  const regularBadges = useMemo(
    () => sortBadgeItems(badges.filter((badge) => !isClubBadge(badge))),
    [badges],
  )
  const clubBadges = useMemo(
    () => sortBadgeItems(badges.filter((badge) => isClubBadge(badge))),
    [badges],
  )
  const visibleBadges = activeCategory === 'club' ? clubBadges : regularBadges
  const hasBadges = badges.length > 0

  useEffect(() => {
    if (activeCategory === 'regular' && regularBadges.length === 0 && clubBadges.length > 0) {
      setActiveCategory('club')
      return
    }

    if (activeCategory === 'club' && clubBadges.length === 0 && regularBadges.length > 0) {
      setActiveCategory('regular')
    }
  }, [activeCategory, clubBadges.length, regularBadges.length])

  useEffect(() => {
    if (!selectedBadge) return
    const nextBadge = badges.find((item) => item.id === selectedBadge.id)
    if (!nextBadge) {
      setSelectedBadge(null)
      return
    }

    setSelectedBadge(nextBadge)
  }, [badges, selectedBadge])

  return (
    <>
      <div className="animate-drawer-backdrop-in fixed inset-0 z-[72] bg-black/88 backdrop-blur-sm" />
      <div className="fixed inset-0 z-[73] overflow-y-auto">
        <div className="mx-auto flex min-h-full w-full max-w-6xl items-start justify-center p-4 pt-5 lg:items-center lg:p-8">
          <div className="animate-modal-panel-in w-full overflow-hidden rounded-[1.25rem] border border-white/[0.06] bg-[#0d1113] shadow-[0_24px_72px_rgba(0,0,0,0.5)]">
            <div className="flex items-start justify-between gap-4 border-b border-[#202427] px-5 py-4 sm:px-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-surface-500">
                  {t('Mijn trofeeën')}
                </p>
                <h2 className="mt-2 text-xl font-black tracking-tight text-white sm:text-2xl">
                  {t('Badge-overzicht in deze poule')}
                </h2>
                <p className="mt-1.5 text-sm text-surface-400">
                  {t('Open badges, clubcontext en verdiende instanties in dezelfde flow als in de app.')}
                </p>
              </div>
              <ModalCloseButton onClick={onClose} />
            </div>

            <div className="px-5 py-4 sm:px-6">
              <div className="flex flex-wrap items-center gap-2">
                <CategoryPill
                  label={t('Regulier')}
                  count={regularBadges.length}
                  active={activeCategory === 'regular'}
                  disabled={regularBadges.length === 0}
                  onClick={() => setActiveCategory('regular')}
                />
                <CategoryPill
                  label={t('Club')}
                  count={clubBadges.length}
                  active={activeCategory === 'club'}
                  disabled={clubBadges.length === 0}
                  onClick={() => setActiveCategory('club')}
                />
              </div>

              {badgesQuery.isLoading && !hasBadges ? (
                <StateCard
                  className="mt-4"
                  title={t('Badges laden…')}
                  body={t('We halen je volledige badgecatalogus voor deze poule op.')}
                />
              ) : null}

              {badgesQuery.isError && !hasBadges ? (
                <StateCard
                  className="mt-4"
                  tone="error"
                  title={t('Badges niet geladen')}
                  body={badgesQuery.error?.message || t('De badges konden niet worden geladen.')}
                  actionLabel={t('Opnieuw proberen')}
                  onAction={() => {
                    void badgesQuery.refetch()
                  }}
                />
              ) : null}

              {!badgesQuery.isLoading && !badgesQuery.isError && !hasBadges ? (
                <StateCard
                  className="mt-4"
                  title={t('Nog geen badges beschikbaar')}
                  body={t('Deze poule heeft nog geen badgecatalogus om te tonen.')}
                />
              ) : null}

              {hasBadges ? (
                <>
                  {badgesQuery.isFetching ? (
                    <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-surface-500">
                      {t('Badges worden ververst…')}
                    </p>
                  ) : null}

                  {visibleBadges.length === 0 ? (
                    <StateCard
                      className="mt-4"
                      title={
                        activeCategory === 'club'
                          ? t('Nog geen club badges')
                          : t('Nog geen reguliere badges')
                      }
                      body={t('Wissel van categorie of kom later terug zodra de catalogus verder is gevuld.')}
                    />
                  ) : (
                    <div className="mt-4 grid grid-cols-3 gap-3 sm:gap-4 lg:grid-cols-5">
                      {visibleBadges.map((badge) => (
                        <button
                          key={badge.id}
                          type="button"
                          onClick={() => setSelectedBadge(badge)}
                          className={[
                            'group flex min-h-[10.75rem] flex-col items-center rounded-[1.1rem] border bg-[#0e1112] p-3 text-center transition',
                            badge.earned
                              ? 'border-[#1c2f43] hover:border-[#2b4d69] hover:bg-[#0e1520]'
                              : 'border-[#171a1b] opacity-60 hover:opacity-80',
                          ].join(' ')}
                        >
                          <div className="flex w-full justify-center">
                            <BadgeArtwork badge={badge} size="tile" />
                          </div>
                          <p className="mt-3 min-h-[2.5rem] text-sm font-black leading-5 text-white">
                            {badge.badge.name}
                          </p>
                          {getTierLabel(badge.tier) ? (
                            <div className="mt-1 flex justify-center">
                              <TierPill
                                label={getTierLabel(badge.tier) as string}
                                tierKey={badge.tier?.key}
                              />
                            </div>
                          ) : null}
                          <p className="mt-1.5 text-xs text-surface-400">
                            {getBadgeTileSubtitle(badge)}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {selectedBadge ? (
        <BadgeDetailSheet badge={selectedBadge} onClose={() => setSelectedBadge(null)} />
      ) : null}
    </>
  )
}

function BadgeDetailSheet({
  badge,
  onClose,
}: {
  badge: BadgeOverviewItem
  onClose: () => void
}) {
  const { t } = useTranslation()
  const clubBadge = isClubBadge(badge)
  const instances = useMemo(() => getVisibleInstances(badge), [badge])

  return (
    <>
      <button
        type="button"
        aria-label={t('Sluit badge details')}
        onClick={onClose}
        className="animate-drawer-backdrop-in fixed inset-0 z-[79] bg-black/55 backdrop-blur-sm"
      />
      <section className="animate-manager-player-picker-in fixed inset-x-0 bottom-0 z-[80] mx-auto w-full max-w-4xl overflow-hidden rounded-t-[1.6rem] border border-[#30383c] bg-[radial-gradient(circle_at_top,_rgba(74,111,124,0.12),_transparent_38%),linear-gradient(180deg,_rgba(8,11,18,1),_rgba(2,4,8,1))] shadow-[0_-20px_60px_rgba(0,0,0,0.45)]">
        <div className="mx-auto h-[72vh] max-w-4xl overflow-y-auto px-5 pb-6 pt-4 sm:px-6">
          <div className="mx-auto mb-4 h-1.5 w-14 rounded-full bg-surface-700" />
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-surface-500">
                {t('Badge detail')}
              </p>
              <h3 className="mt-2 text-2xl font-black tracking-tight text-white">
                {badge.badge.name}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {getTierLabel(badge.tier) ? (
                  <TierPill
                    label={getTierLabel(badge.tier) as string}
                    tierKey={badge.tier?.key}
                  />
                ) : null}
                {getDifficultyLabel(badge.badge.difficulty) ? (
                  <span className="rounded-full border border-surface-700 bg-surface-900 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-surface-300">
                    {getDifficultyLabel(badge.badge.difficulty)}
                  </span>
                ) : null}
                <span className="rounded-full border border-[#202427] bg-[#0d1113] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-surface-400">
                  {clubBadge ? t('Club') : t('Regulier')}
                </span>
              </div>
            </div>
            <ModalCloseButton onClick={onClose} />
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-4">
              <div className="rounded-[1.25rem] border border-[#202427] bg-[#101416] p-5">
                <div className="flex justify-center">
                  <BadgeArtwork badge={badge} size="detail" />
                </div>
                <p className="mt-4 text-sm leading-6 text-surface-300">
                  {badge.badge.description?.trim() || t('Nog geen beschrijving beschikbaar voor deze badge.')}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <InfoCard label={t('Status')} value={badge.earned ? t('Verdiend') : t('Nog niet verdiend')} />
                <InfoCard
                  label={t('Aantal keer')}
                  value={badge.earned ? `${badge.earned_count}x` : '0x'}
                />
                {badge.last_earned_at ? (
                  <InfoCard label={t('Laatste keer')} value={formatDateTime(badge.last_earned_at)} />
                ) : null}
                {badge.club?.name?.trim() ? (
                  <InfoCard label={t('Clubcontext')} value={badge.club.name.trim()} />
                ) : null}
              </div>

              <div className="rounded-[1.1rem] border border-[#202427] bg-[#101416] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-surface-500">
                  {t('Ook verdiend door')}
                </p>
                {badge.earned_by_users.length === 0 ? (
                  <p className="mt-2 text-sm text-surface-400">
                    {t('Nog geen andere gebruikers in deze poule zichtbaar voor deze badge.')}
                  </p>
                ) : (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {badge.earned_by_users.slice(0, 8).map((user) => (
                      <BadgeUserAvatar key={user.id} user={user} />
                    ))}
                    {badge.earned_by_users_count > badge.earned_by_users.length ? (
                      <span className="flex h-11 min-w-11 items-center justify-center rounded-full bg-[#0d1520] px-3 text-xs font-black text-white">
                        +{badge.earned_by_users_count - badge.earned_by_users.length}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {!badge.earned ? (
                <div className="rounded-[1.1rem] border border-[#202427] bg-[#101416] p-4">
                  <p className="text-base font-black text-white">{t('Nog niet verdiend')}</p>
                  <p className="mt-2 text-sm text-surface-400">
                    {t('Zodra je deze badge vrijspeelt, verschijnen hier de verdiende instanties en bijbehorende wedstrijden.')}
                  </p>
                </div>
              ) : null}

              {badge.earned && instances.length > 0 ? (
                <div className="space-y-4">
                  {instances.map((instance, index) => (
                    <BadgeInstanceCard
                      key={`${badge.id}-${instance.id}-${index}`}
                      badgeId={badge.id}
                      instance={instance}
                      index={index}
                      clubBadge={clubBadge}
                    />
                  ))}
                </div>
              ) : null}

              {badge.earned ? (
                <button
                  type="button"
                  className="flex w-full items-center justify-center rounded-[1rem] border border-[#203841] bg-[#10191c] px-4 py-3 text-sm font-black text-white transition hover:bg-[#0d2232]"
                >
                  {t('Deel badge')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

function BadgeArtwork({
  badge,
  size,
}: {
  badge: BadgeOverviewItem
  size: 'tile' | 'detail'
}) {
  const { t } = useTranslation()
  const clubBadge = isClubBadge(badge)
  const visualUrl = getBadgeDisplayImage(badge)
  const dimensions =
    size === 'detail'
      ? 'h-28 w-28 rounded-[1.35rem] sm:h-32 sm:w-32'
      : 'h-14 w-14 rounded-[1rem] sm:h-16 sm:w-16'
  const accent = getTierAccentColors(badge.tier?.key)

  return (
    <span
      className={`inline-flex shrink-0 ${dimensions} ${clubBadge ? 'p-[2px]' : 'bg-[#171a1b] p-[1px]'}`}
      style={
        clubBadge
          ? { background: `linear-gradient(135deg, ${accent.start}, ${accent.end})` }
          : undefined
      }
    >
      <span className="flex h-full w-full items-center justify-center rounded-[inherit] bg-[#111517]">
        {visualUrl ? (
          <img src={visualUrl} alt="" className="h-[calc(100%-0.1rem)] w-[calc(100%-0.1rem)] rounded-[inherit] object-cover" />
        ) : (
          <span className="text-2xl text-surface-300 sm:text-3xl">🏆</span>
        )}
      </span>
    </span>
  )
}

function PreviewBadgeArtwork({ badge }: { badge: Record<string, unknown> }) {
  const nestedBadge = badge.badge && typeof badge.badge === 'object' && !Array.isArray(badge.badge)
    ? (badge.badge as Record<string, unknown>)
    : undefined
  const club = badge.club && typeof badge.club === 'object' && !Array.isArray(badge.club)
    ? (badge.club as Record<string, unknown>)
    : undefined
  const image = [
    badge.image_url,
    badge.icon_url,
    badge.icon,
    badge.logo_url,
    club?.logo,
    nestedBadge?.image_url,
    nestedBadge?.icon_url,
    nestedBadge?.icon,
    nestedBadge?.logo_url,
  ].find((value): value is string => typeof value === 'string' && value.trim().length > 0)

  return image ? (
    <img src={image} alt="" className="h-14 w-14 rounded-[0.9rem] object-contain" />
  ) : (
    <span className="text-3xl" aria-hidden="true">🏆</span>
  )
}

function CategoryPill({
  label,
  count,
  active,
  disabled,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'rounded-full px-4 py-2 text-sm font-semibold transition',
        active
          ? 'bg-[#3f7890] text-white shadow-[0_10px_24px_rgba(74,111,124,0.16)]'
          : 'border border-[#30383c] bg-[#0d1113] text-surface-400 hover:text-white',
        'disabled:cursor-default disabled:opacity-45',
      ].join(' ')}
    >
      {label} <span className="ml-1 text-xs opacity-80">{count}</span>
    </button>
  )
}

function TierPill({
  label,
  tierKey,
}: {
  label: string
  tierKey?: string | null | undefined
}) {
  const colors = getTierAccentColors(tierKey)

  return (
    <span
      className="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white"
      style={{
        background: `linear-gradient(135deg, ${colors.start}, ${colors.end})`,
      }}
    >
      {label}
    </span>
  )
}

function BadgeUserAvatar({ user }: { user: BadgeUser }) {
  const label = normalizeString(user.display_name) || normalizeString(user.name) || translate('Speler')

  return (
    <div className="flex items-center gap-2 rounded-full bg-[#0d1520] pr-3">
      {user.profile_image ? (
        <img src={user.profile_image} alt="" className="h-11 w-11 rounded-full object-cover" />
      ) : (
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#191c1d] text-sm font-black uppercase text-surface-300">
          {getInitials(label)}
        </span>
      )}
      <span className="text-sm font-semibold text-white">{label}</span>
    </div>
  )
}

function BadgeMatchCard({ match }: { match: FootballMatch }) {
  return (
    <div className="rounded-[1rem] border border-[#1c2737] bg-[linear-gradient(180deg,_rgba(10,15,22,0.96),_rgba(6,10,16,0.98))] px-4 py-3 shadow-[0_10px_26px_rgba(0,0,0,0.18)]">
      <div className="flex items-start gap-3">
        <div className="w-14 shrink-0 pt-0.5 text-center">
          <p className="text-xs font-bold text-white">
            {formatKickoff(match.kickoff_utc, match.kickoff_nl)}
          </p>
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-2.5">
            <TeamRow
              team={match.home_team}
              score={match.score?.home}
              pointsBadgeValue={match.user_prediction?.points_awarded ?? 0}
            />
            <TeamRow team={match.away_team} score={match.score?.away} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {match.status !== 'ft' && match.status !== 'live' && match.status !== 'ht' ? (
              <span className="inline-flex rounded-full border border-surface-700 bg-surface-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
                {getMatchStatusLabel(match)}
              </span>
            ) : null}
            {(typeof match.user_prediction?.home_pred === 'number' ||
              typeof match.user_prediction?.away_pred === 'number') ? (
              <span className="inline-flex rounded-full border border-[#202427] bg-[#0d1113] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-300">
                {match.user_prediction?.home_pred ?? '–'}-{match.user_prediction?.away_pred ?? '–'}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function BadgeInstanceCard({
  badgeId,
  instance,
  index,
  clubBadge,
}: {
  badgeId: number
  instance: UserBadgeInstance
  index: number
  clubBadge: boolean
}) {
  const { t } = useTranslation()
  const pouleLabel = getPouleLabel(instance)
  const contextLabel = getInstanceContextLabel(instance)
  const matches = getRelatedMatches(instance)

  return (
    <article className="rounded-[1.1rem] border border-[#202427] bg-[#101416] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-surface-500">
            {getInstanceHeader(instance, index, clubBadge)}
          </p>
          <p className="mt-1 text-sm font-semibold text-white">
            {instance.reason?.trim() || t('Badge verdiend')}
          </p>
        </div>
        <span className="text-xs font-semibold text-surface-400">
          {formatDateTime(instance.earned_at)}
        </span>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {pouleLabel ? <InfoCard label={t('Poule')} value={pouleLabel} /> : null}
        {contextLabel ? <InfoCard label={t('Context')} value={contextLabel} /> : null}
      </div>

      {matches.length > 0 ? (
        <div className="mt-4 space-y-2">
          {matches.map((match) => (
            <BadgeMatchCard key={`${badgeId}-${instance.id}-${match.id}`} match={match} />
          ))}
        </div>
      ) : null}
    </article>
  )
}

function TeamRow({
  team,
  score,
  pointsBadgeValue,
}: {
  team: FootballMatch['home_team']
  score?: number | null | undefined
  pointsBadgeValue?: number | undefined
}) {
  return (
    <div className="flex items-center gap-2">
      {team.logo_url ? (
        <img src={team.logo_url} alt="" className="h-8 w-8 rounded-md object-cover" />
      ) : (
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-surface-800 text-[10px] font-black uppercase text-surface-300">
          {getInitials(team.name)}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-white sm:text-base">{team.name}</span>
      {typeof pointsBadgeValue === 'number' ? <PointsBadge points={pointsBadgeValue} /> : null}
      <span className="min-w-[1.5rem] text-right text-base font-black text-white">{typeof score === 'number' ? score : '–'}</span>
    </div>
  )
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[1rem] bg-[#111517] px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
        {label}
      </p>
      <p className="mt-1.5 text-sm font-semibold text-white">{value}</p>
    </div>
  )
}

function StateCard({
  className,
  title,
  body,
  tone = 'neutral',
  actionLabel,
  onAction,
}: {
  className?: string
  title: string
  body: string
  tone?: 'neutral' | 'error'
  actionLabel?: string
  onAction?: () => void
}) {
  return (
    <div
      className={[
        'rounded-[1rem] border px-4 py-5',
        tone === 'error'
          ? 'border-red-900/60 bg-red-950/25 text-red-100'
          : 'border-[#202427] bg-[#101416] text-surface-100',
        className ?? '',
      ].join(' ')}
    >
      <p className="text-base font-black text-inherit">{title}</p>
      <p className="mt-2 text-sm leading-6 text-inherit/80">{body}</p>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="mt-4 rounded-full border border-current/20 px-4 py-2 text-sm font-semibold text-inherit transition hover:bg-white/5"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  )
}

function sortBadgeItems(items: BadgeOverviewItem[]) {
  return [...items].sort((left, right) => {
    if (left.earned !== right.earned) return left.earned ? -1 : 1

    const timeDiff = getTimeValue(right.last_earned_at) - getTimeValue(left.last_earned_at)
    if (timeDiff !== 0) return timeDiff

    return left.badge.name.localeCompare(right.badge.name, 'nl')
  })
}

function isClubBadge(badge: BadgeOverviewItem) {
  if (normalizeString(badge.badge.category)?.toLowerCase() === 'club') return true
  if (badge.club?.id != null) return true
  return badge.earned_instances.some((instance) => hasBadgeClubContext(instance))
}

function hasBadgeClubContext(instance: UserBadgeInstance) {
  return (
    instance.club?.id != null ||
    Boolean(getMetaTeamId(instance.meta)) ||
    Boolean(getMetaString(instance.meta, ['team_name', 'team_display_name', 'team_logo']))
  )
}

function getBadgeTileSubtitle(badge: BadgeOverviewItem) {
  if (!badge.earned) return translate('Nog niet verdiend')
  if (!isClubBadge(badge) && badge.earned_count > 1) return translate('{count}x behaald', getLocale(), { count: badge.earned_count })
  if (badge.last_earned_at) return translate('Laatste keer {date}', getLocale(), { date: formatShortDate(badge.last_earned_at) })
  return translate('Verdiend')
}

function getBadgeDisplayImage(badge: BadgeOverviewItem) {
  const latestInstance = getLatestEarnedInstance(badge)
  return (
    getBadgeInstanceImage(latestInstance, badge.badge.icon_url) ??
    normalizeString(badge.club?.logo) ??
    normalizeString(badge.badge.icon_url)
  )
}

function getLatestEarnedInstance(badge: BadgeOverviewItem) {
  return [...badge.earned_instances].sort(
    (left, right) => getTimeValue(right.earned_at) - getTimeValue(left.earned_at),
  )[0]
}

function getBadgeInstanceImage(
  instance?: UserBadgeInstance | null,
  fallbackIcon?: string | null | undefined,
) {
  if (!instance) return normalizeString(fallbackIcon)

  const teamId = getMetaTeamId(instance.meta)
  const teamName = getMetaString(instance.meta, ['team_display_name', 'team_name'])
  const matches = getRelatedMatches(instance)

  if (teamId != null) {
    for (const match of matches) {
      if (match.home_team.id === teamId && normalizeString(match.home_team.logo_url)) {
        return normalizeString(match.home_team.logo_url)
      }
      if (match.away_team.id === teamId && normalizeString(match.away_team.logo_url)) {
        return normalizeString(match.away_team.logo_url)
      }
    }
  }

  if (teamName) {
    for (const match of matches) {
      if (match.home_team.name === teamName && normalizeString(match.home_team.logo_url)) {
        return normalizeString(match.home_team.logo_url)
      }
      if (match.away_team.name === teamName && normalizeString(match.away_team.logo_url)) {
        return normalizeString(match.away_team.logo_url)
      }
    }
  }

  return (
    getMetaString(instance.meta, ['team_logo']) ??
    normalizeString(instance.club?.logo) ??
    normalizeString(instance.badge?.icon_url) ??
    normalizeString(fallbackIcon)
  )
}

function getVisibleInstances(badge: BadgeOverviewItem) {
  const sorted = [...badge.earned_instances].sort(
    (left, right) => getTimeValue(left.earned_at) - getTimeValue(right.earned_at),
  )

  if (!isClubBadge(badge)) return sorted
  const latest = sorted.at(-1)
  return latest ? [latest] : []
}

function getRelatedMatches(instance: UserBadgeInstance) {
  const source = instance.match ? [instance.match, ...(instance.matches ?? [])] : instance.matches ?? []
  const seen = new Set<number>()

  return source.filter((match) => {
    if (seen.has(match.id)) return false
    seen.add(match.id)
    return true
  })
}

function getInstanceHeader(
  instance: UserBadgeInstance,
  index: number,
  clubBadge: boolean,
) {
  if (clubBadge) return translate('Meest recent verdiend')
  if (getMetaString(instance.meta, ['day_id'])) return formatShortDate(instance.earned_at)

  const round = getMetaString(instance.meta, ['round', 'round_name'])
  if (round) return translate('Ronde {number}', getLocale(), { number: round })

  return translate('{ordinal} keer behaald', getLocale(), { ordinal: getOrdinal(index + 1) })
}

function getInstanceContextLabel(instance: UserBadgeInstance) {
  return (
    getMetaString(instance.meta, ['team_display_name', 'team_name']) ??
    normalizeString(instance.club?.name)
  )
}

function getPouleLabel(instance: UserBadgeInstance) {
  return normalizeString(instance.poule?.name)
}

function getTierLabel(tier?: { label?: string | null | undefined } | null) {
  return normalizeString(tier?.label)
}

function getDifficultyLabel(value?: number | null) {
  return typeof value === 'number' ? translate('Moeilijkheid {value}', getLocale(), { value }) : undefined
}

function getMatchStatusLabel(match: FootballMatch) {
  if (match.status === 'ft') return translate('Eindstand')
  if (match.status === 'live') {
    return typeof match.minute === 'number'
      ? typeof match.extra_minute === 'number' && match.extra_minute > 0
        ? `${match.minute}+${match.extra_minute}'`
        : `${match.minute}'`
      : translate('Live')
  }
  if (match.status === 'ht') return translate('Rust')
  if (match.status === 'cancelled') return translate('Geannuleerd')
  if (match.status === 'postponed') return translate('Uitgesteld')
  return translate('Gepland')
}

function formatKickoff(kickoffUtc?: string | null, kickoffFallback?: string | null) {
  return formatKickoffCompactDateTime(kickoffUtc, kickoffFallback)
}

function formatDateTime(value?: string | null) {
  if (!value) return translate('Onbekend moment')
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(toIntlLocale(getLocale()), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function formatShortDate(value?: string | null) {
  if (!value) return translate('Onbekend')
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(toIntlLocale(getLocale()), {
    day: 'numeric',
    month: 'short',
  }).format(date)
}

function getOrdinal(value: number) {
  if (value <= 0) return `${value}e`
  if (value === 1) return '1e'
  if (value === 2) return '2e'
  if (value === 3) return '3e'
  return `${value}e`
}

function getTimeValue(value?: string | null) {
  if (!value) return 0
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

function getMetaTeamId(meta?: Record<string, unknown> | null) {
  const raw = meta?.team_id
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

function getMetaString(
  meta: Record<string, unknown> | null | undefined,
  keys: string[],
) {
  for (const key of keys) {
    const value = meta?.[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }

  return undefined
}

function getTierAccentColors(tierKey?: string | null | undefined) {
  const normalized = normalizeString(tierKey)?.toLowerCase()
  if (normalized === 'gold') {
    return { start: '#e7b84c', end: '#8b6420' }
  }
  if (normalized === 'silver') {
    return { start: '#cfd8e6', end: '#6d7892' }
  }
  if (normalized === 'bronze') {
    return { start: '#c98b4a', end: '#6f4020' }
  }

  return { start: '#4acbff', end: '#1f6d96' }
}

function findPreviewLabel(payload: Record<string, unknown>) {
  const candidates = ['name', 'title']
  for (const candidate of candidates) {
    const value = payload[candidate]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }

  return undefined
}

function findPreviewDescription(payload: Record<string, unknown>) {
  const candidates = ['description', 'reason']
  for (const candidate of candidates) {
    const value = payload[candidate]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }

  return undefined
}

function normalizeString(value?: string | null) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

function getInitials(value: string) {
  return value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}
